import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { states, USER_LOAN_PROGRAM, USER_OCCUPANCY } from '@/config/constants';
import { useCreateProductMutation } from '@/apis/extended/productApi';
import {
  useGetCurrentUserQuery,
  useUpdateUserLoanInfoMutation,
} from '@/apis/extended/userApi';
import {
  qualificationFormFromUser,
  qualificationRequest,
  qualificationTotals,
  RefinanceQualificationFields,
  type QualificationForm,
} from '@/components/Refinance/RefinanceQualificationFields';
import { qualifiesWithPfs } from '@/utils/borrowerType';
import type { OnboardingIntent } from '@/types/UserType';
import { CommonDialog } from '../Common/CommonDialog';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';

type PropertyKind = 'purchase' | 'refinance' | 'investment';
type InvestmentTxn = 'purchase' | 'refinance';

/** @deprecated Use PropertyKind — kept for callers passing `initialMode="dscr"`. */
export type AddPropertyInitialMode = 'purchase' | 'refinance' | 'dscr';

interface AddPropertyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (productId: string) => void;
  initialMode?: AddPropertyInitialMode;
}

const initialAddress = { street: '', city: '', state: '', zip: '' };

/**
 * Start on what the borrower told us they came to do — a homeowner who chose
 * "Refinance" shouldn't have to notice a Purchase default and switch it.
 */
function initialKindFromMode(
  mode?: AddPropertyInitialMode,
  presetRefi?: boolean,
  intent?: OnboardingIntent | null,
  commercial = false
): PropertyKind {
  if (mode === 'dscr' || commercial) return 'investment';
  if (mode === 'refinance' || presetRefi) return 'refinance';
  if (mode === 'purchase') return 'purchase';
  if (intent === 'refinance' || intent === 'rate_watch') return 'refinance';
  if (intent === 'investment') return 'investment';
  return 'purchase';
}

type RefiGoal = 'rate_and_term' | 'cash_out';

function resolveTransactionMode(
  kind: PropertyKind,
  investmentTxn: InvestmentTxn
): 'purchase' | 'refinance' {
  if (kind === 'refinance') return 'refinance';
  if (kind === 'investment') return investmentTxn;
  return 'purchase';
}

const toPositive = (v: string) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

export const AddPropertyDialog = ({
  open,
  onClose,
  onCreated,
  initialMode,
}: AddPropertyDialogProps) => {
  const [searchParams] = useSearchParams();
  const presetRefi = searchParams.get('isRefinance') === '1';
  const { data: user } = useGetCurrentUserQuery();
  const intent = user?.onboardingIntent ?? null;
  const isCommercial = qualifiesWithPfs(user);
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const [updateUserLoanInfo] = useUpdateUserLoanInfoMutation();
  const [kind, setKind] = useState<PropertyKind>(() =>
    initialKindFromMode(initialMode, presetRefi, intent, isCommercial)
  );
  const [balance, setBalance] = useState('');
  const [rate, setRate] = useState('');
  const [payment, setPayment] = useState('');
  const [yearsLeft, setYearsLeft] = useState('');
  const [qualification, setQualification] = useState<QualificationForm>(() =>
    qualificationFormFromUser(user)
  );
  const [refiGoal, setRefiGoal] = useState<RefiGoal>('rate_and_term');
  const [cashOut, setCashOut] = useState('');
  const balanceRef = useRef<HTMLInputElement>(null);
  const [investmentTxn, setInvestmentTxn] = useState<InvestmentTxn>('purchase');
  const [price, setPrice] = useState('');
  const [address, setAddress] = useState({ ...initialAddress });
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [missingFieldLabels, setMissingFieldLabels] = useState<string[]>([]);

  const priceRef = useRef<HTMLInputElement>(null);
  const streetRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);

  const isRefinanceContext =
    kind === 'refinance' ||
    (kind === 'investment' && investmentTxn === 'refinance');

  useEffect(() => {
    if (open) {
      setKind(
        initialKindFromMode(initialMode, presetRefi, intent, isCommercial)
      );
      setInvestmentTxn('purchase');
    }
  }, [open, initialMode, presetRefi, intent, isCommercial]);

  // Prefill credit, income and debts from the profile each time it opens.
  useEffect(() => {
    if (open) setQualification(qualificationFormFromUser(user));
  }, [open, user]);

  const setAddressField =
    (field: keyof typeof initialAddress) => (value: string) => {
      setAddress((a) => ({ ...a, [field]: value }));
    };

  // The payoff is the whole point of a refinance estimate; without it the
  // property page showed a $0 payoff and the full loan as "cash back".
  const isValid = Boolean(
    price &&
      address.street &&
      address.city &&
      address.state &&
      address.zip &&
      (!isRefinanceContext || balance !== '')
  );

  const reset = () => {
    setName('');
    setPrice('');
    setAddress({ ...initialAddress });
    setInvestmentTxn('purchase');
    setBalance('');
    setRate('');
    setPayment('');
    setYearsLeft('');
    setRefiGoal('rate_and_term');
    setCashOut('');
    setSubmitted(false);
    setMissingFieldLabels([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    setSubmitted(true);
    if (!isValid) {
      const missing = [
        {
          present: Boolean(price),
          label: isRefinanceContext ? 'Estimated home value' : 'Listing price',
          ref: priceRef,
        },
        { present: Boolean(address.street), label: 'Street', ref: streetRef },
        { present: Boolean(address.city), label: 'City', ref: cityRef },
        { present: Boolean(address.state), label: 'State', ref: stateRef },
        { present: Boolean(address.zip), label: 'ZIP', ref: zipRef },
        {
          present: !isRefinanceContext || balance !== '',
          label: 'Current mortgage balance',
          ref: balanceRef,
        },
      ].filter((field) => !field.present);
      setMissingFieldLabels(missing.map((field) => field.label));
      missing[0]?.ref.current?.focus();
      return;
    }
    setMissingFieldLabels([]);
    const fullAddress = `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
    const transactionMode = resolveTransactionMode(kind, investmentTxn);
    try {
      const toNum = (v: string) => (v === '' ? undefined : parseFloat(v));
      // A home the borrower lives in qualifies on their income and debts;
      // save them to the profile the estimates read DTI from.
      const isPrimaryRefi = kind === 'refinance';
      const qualificationTotalsNow = qualificationTotals(qualification);
      if (isPrimaryRefi) {
        // Don't lose the property over the profile save.
        await updateUserLoanInfo(qualificationRequest(qualification))
          .unwrap()
          .catch(() => toast.error('Could not save your income and debts'));
      }
      const created = await createProduct({
        name: name || fullAddress,
        price: parseFloat(price),
        transactionMode,
        ...(kind === 'investment'
          ? {
              savedLoanType: isCommercial
                ? USER_LOAN_PROGRAM.COMMERCIAL
                : USER_LOAN_PROGRAM.DSCR,
            }
          : {}),
        ...(transactionMode === 'refinance'
          ? {
              refinance: {
                currentLoan: {
                  balance: toNum(balance),
                  rate: toNum(rate),
                  monthlyPayment: toNum(payment),
                  termYears: toNum(yearsLeft),
                },
                newLoan: {
                  ...(refiGoal === 'cash_out'
                    ? { cashOutAmount: toNum(cashOut) }
                    : {}),
                  ...(isPrimaryRefi && qualificationTotalsNow.creditScore
                    ? { creditScore: qualificationTotalsNow.creditScore }
                    : {}),
                },
                selection: {
                  refiType: refiGoal,
                  ...(isPrimaryRefi
                    ? { occupancy: USER_OCCUPANCY.PRIMARY_RESIDENCE }
                    : {}),
                },
              },
            }
          : {}),
        address: { ...address, fullAddress },
      }).unwrap();
      const id =
        (created as { _id?: string; id?: string })._id ??
        (created as { id?: string }).id;
      if (id) {
        toast.success('Property saved');
        reset();
        onCreated?.(id);
      }
    } catch (err) {
      toast.error('Could not save property');
      console.error(err);
    }
  };

  return (
    <CommonDialog
      isOpen={open}
      title="Add Property"
      icon="fa-light fa-house-circle-check"
      confirmButtonText="Save property"
      closeButtonText="Cancel"
      isLoading={isLoading}
      isConfirmDisabled={isLoading}
      onConfirm={handleCreate}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '640px',
          maxWidth: '92vw',
        },
      }}
    >
      <Stack gap={2}>
        {submitted && missingFieldLabels.length > 0 ? (
          <Alert severity="error">
            Please fill in: {missingFieldLabels.join(', ')}
          </Alert>
        ) : null}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Are you buying this home or do you already own it?
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={kind}
            exclusive
            onChange={(_, v) => v && setKind(v as PropertyKind)}
            size="small"
          >
            <ToggleButton value="purchase">Buying it</ToggleButton>
            <ToggleButton value="refinance">I own it (refinance)</ToggleButton>
            <ToggleButton value="investment">Investment</ToggleButton>
          </ToggleButtonGroup>

          {kind === 'investment' ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
              >
                Are you buying or refinancing this investment?
              </Typography>
              <ToggleButtonGroup
                color="primary"
                value={investmentTxn}
                exclusive
                onChange={(_, v) => v && setInvestmentTxn(v as InvestmentTxn)}
                size="small"
              >
                <ToggleButton value="purchase">Purchase</ToggleButton>
                <ToggleButton value="refinance">Refinance</ToggleButton>
              </ToggleButtonGroup>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                Saved with investor / DSCR loan metrics on the next screen.
              </Typography>
            </Box>
          ) : null}
        </Box>

        <CommonFormControl
          label={isRefinanceContext ? 'Estimated home value' : 'Listing price'}
          error={submitted && !price ? 'Required' : ''}
        >
          <CommonNumberInput
            name="price"
            aria-label={
              isRefinanceContext ? 'Estimated home value' : 'Listing price'
            }
            required
            inputRef={priceRef}
            value={price}
            placeholder="$"
            fullWidth
            onChange={(e) => setPrice(e.target.value)}
          />
        </CommonFormControl>

        <CommonFormControl
          label="Street"
          error={submitted && !address.street ? 'Required' : ''}
        >
          <TextField
            name="street"
            aria-label="Street address"
            required
            inputRef={streetRef}
            fullWidth
            value={address.street}
            onChange={(e) => setAddressField('street')(e.target.value)}
          />
        </CommonFormControl>

        <CommonFormControl
          label="City"
          error={submitted && !address.city ? 'Required' : ''}
        >
          <TextField
            name="city"
            aria-label="City"
            required
            inputRef={cityRef}
            fullWidth
            value={address.city}
            onChange={(e) => setAddressField('city')(e.target.value)}
          />
        </CommonFormControl>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CommonFormControl
              label="State"
              error={submitted && !address.state ? 'Required' : ''}
            >
              {/* Same outlined TextField as Street/City/ZIP — the bare
                  Select had no visible border and nearly disappeared. */}
              <TextField
                select
                name="state"
                aria-label="State"
                required
                inputRef={stateRef}
                value={address.state}
                onChange={(e) => setAddressField('state')(e.target.value)}
                fullWidth
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value ? (
                      states.find((s) => s.value === value)?.label ??
                      String(value)
                    ) : (
                      <span style={{ opacity: 0.6 }}>Select state</span>
                    ),
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </CommonFormControl>
          </Grid>
          <Grid item xs={6}>
            <CommonFormControl
              label="ZIP"
              error={submitted && !address.zip ? 'Required' : ''}
            >
              <TextField
                name="zip"
                aria-label="ZIP code"
                required
                inputRef={zipRef}
                fullWidth
                inputProps={{ maxLength: 5 }}
                value={address.zip}
                onChange={(e) => setAddressField('zip')(e.target.value)}
              />
            </CommonFormControl>
          </Grid>
        </Grid>

        {isRefinanceContext ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Your current mortgage
            </Typography>
            <Stack gap={2}>
              <CommonFormControl
                label="Current loan balance"
                error={submitted && balance === '' ? 'Required' : ''}
              >
                <CommonNumberInput
                  name="balance"
                  aria-label="Current loan balance"
                  inputRef={balanceRef}
                  value={balance}
                  placeholder="$  (enter 0 if it's paid off)"
                  fullWidth
                  onChange={(e) => setBalance(e.target.value)}
                />
              </CommonFormControl>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CommonFormControl label="Interest rate (optional)">
                    <CommonNumberInput
                      name="rate"
                      aria-label="Current interest rate"
                      value={rate}
                      placeholder="%"
                      fullWidth
                      onChange={(e) => setRate(e.target.value)}
                    />
                  </CommonFormControl>
                </Grid>
                <Grid item xs={6}>
                  <CommonFormControl label="Monthly P&I payment (optional)">
                    <CommonNumberInput
                      name="payment"
                      aria-label="Current monthly payment"
                      value={payment}
                      placeholder="$"
                      fullWidth
                      onChange={(e) => setPayment(e.target.value)}
                    />
                  </CommonFormControl>
                </Grid>
              </Grid>
              <CommonFormControl label="Years left on the loan (optional)">
                <CommonNumberInput
                  name="yearsLeft"
                  aria-label="Years left on the loan"
                  value={yearsLeft}
                  placeholder="e.g. 27"
                  fullWidth
                  onChange={(e) => setYearsLeft(e.target.value)}
                />
              </CommonFormControl>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
                >
                  What do you want the refinance to do?
                </Typography>
                <ToggleButtonGroup
                  color="primary"
                  value={refiGoal}
                  exclusive
                  onChange={(_, v) => v && setRefiGoal(v as RefiGoal)}
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="rate_and_term">
                    Lower my rate or payment
                  </ToggleButton>
                  <ToggleButton value="cash_out">Take cash out</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {refiGoal === 'cash_out' ? (
                <CommonFormControl label="Cash out amount (optional)">
                  <CommonNumberInput
                    name="cashOut"
                    aria-label="Cash out amount"
                    value={cashOut}
                    placeholder="$"
                    fullWidth
                    onChange={(e) => setCashOut(e.target.value)}
                  />
                </CommonFormControl>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        {kind === 'refinance' ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Will you qualify?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Lenders check your credit and that your debts, including the new
              mortgage, fit your income. We use this to price your options and
              show your debt-to-income ratio.
            </Typography>
            <RefinanceQualificationFields
              value={qualification}
              onChange={setQualification}
              housingPayment={toPositive(payment)}
            />
          </Box>
        ) : null}

        <CommonFormControl label="Nickname (optional)">
          <TextField
            name="nickname"
            aria-label="Nickname (optional)"
            fullWidth
            placeholder="Defaults to the address"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </CommonFormControl>
        {!isRefinanceContext ? (
          <Typography variant="caption" color="text.secondary">
            Shopping on Zillow or Redfin?{' '}
            <a
              href={
                process.env.VITE_EXTENSION_URL ||
                'https://chromewebstore.google.com/detail/unbeatable-extension/effibccnihcalkgiimempmhiabigndfl'
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Our Chrome extension
            </a>{' '}
            saves listings in one click.
          </Typography>
        ) : null}
      </Stack>
    </CommonDialog>
  );
};
